'use client'

import { useState, useContext } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/shadcn/dialog'
import { Button } from '@/components/ui/shadcn/button'
import { Input } from '@/components/ui/shadcn/input'
import { Label } from '@/components/ui/shadcn/label'
import { Separator } from '@/components/ui/shadcn/separator'
import { AlertCircle } from 'lucide-react'
import { AuthContext } from '@/contexts/AuthContext'
import { fetcher } from '@/lib/api/fetch'

export default function ConfirmDelete({
  open,
  setOpen,
  onComplete,
  reference,
  title,
  description,
  buttonText,
  fieldLabel,
  fieldType,
  apiPath,
  onCancel,
}) {
  const authContext = useContext(AuthContext)
  const [error, setError] = useState(null)

  const submit = async e => {
    e.preventDefault()

    // Check the email address address matches the user's email address
    if (e.target.confirm.value !== reference) {
      setError(`${fieldLabel} does not match`)
      return
    }

    // Call the API to remove the user
    const res = await fetcher(apiPath, { method: 'DELETE' }, authContext)

    // If the API call was successful, call the onComplete
    if (res.ok) {
      onComplete()
      setOpen(false)
    } else {
      // If the API call failed, show the error
      const data = await res.json()
      setError(data.message)
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={v => {
        if (!v) {
          setOpen(false)
          onCancel()
        }
      }}
    >
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {description && <DialogDescription>{description}</DialogDescription>}
        </DialogHeader>
        <Separator />
        {error && (
          <div className="flex items-start gap-2 rounded-md border border-destructive/20 bg-destructive/5 p-3 text-sm text-destructive">
            <AlertCircle className="size-4 mt-0.5 shrink-0" />
            {error}
          </div>
        )}
        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="confirm">{fieldLabel}</Label>
            <Input id="confirm" name="confirm" type={fieldType} required />
          </div>
          <DialogFooter className="flex flex-col gap-2 sm:flex-col">
            <Button type="submit" variant="destructive" className="w-full">
              {buttonText}
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setOpen(false)
                onCancel()
              }}
              className="w-full"
            >
              Cancel
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
